import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/bartender/tips")({
  head: () => ({ meta: [{ title: "My Tips — The Hospitality Reserve" }] }),
  component: BartenderTipsPage,
});

type Row = {
  payout_id: string;
  shift_id: string;
  amount_pence: number;
  payout_status: string; // payable | paid_out | failed | etc
  failure_reason: string | null;
  paid_out_at: string | null;
  created_at: string;
  shift_start: string | null;
  shift_end: string | null;
  role_type: string | null;
  venue_name: string | null;
  tip_allocation_status: string | null;
};

function statusLabel(payout: string, alloc: string | null): { label: string; tone: string } {
  if (payout === "paid_out") return { label: "Succeeded", tone: "text-emerald-700" };
  if (payout === "failed") return { label: "Failed", tone: "text-destructive" };
  if (payout === "payable" || payout === "processing") return { label: "Pending payout", tone: "text-accent" };
  if (alloc === "paid") return { label: "Pending payout", tone: "text-accent" };
  return { label: payout, tone: "text-muted-foreground" };
}

function BartenderTipsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
      if (!u.user) { navigate({ to: "/auth", replace: true }); return; }
      // Pull every tip-kind payout for this bartender, then enrich with shift + venue + tip_allocation.
      const { data: payouts } = await supabase
        .from("payouts")
        .select("id, shift_id, amount_pence, status, failure_reason, paid_out_at, created_at")
        .eq("bartender_id", u.user.id)
        .eq("kind", "tip")
        .order("created_at", { ascending: false })
        .limit(100);
      const list = payouts ?? [];
      const shiftIds = Array.from(new Set(list.map((p) => p.shift_id as string)));
      const [{ data: shifts }, { data: allocs }] = await Promise.all([
        shiftIds.length
          ? supabase.from("shifts").select("id, start_time, end_time, role_type, venue_id").in("id", shiftIds)
          : Promise.resolve({ data: [] as Array<{ id: string; start_time: string; end_time: string; role_type: string; venue_id: string }> }),
        shiftIds.length
          ? supabase.from("tip_allocations").select("shift_id, status").in("shift_id", shiftIds)
          : Promise.resolve({ data: [] as Array<{ shift_id: string; status: string }> }),
      ]);
      const venueIds = Array.from(new Set((shifts ?? []).map((s) => s.venue_id as string)));
      const { data: venues } = venueIds.length
        ? await supabase.from("profiles").select("id, full_name").in("id", venueIds)
        : { data: [] as Array<{ id: string; full_name: string }> };
      const shiftMap = new Map((shifts ?? []).map((s) => [s.id, s]));
      const venueMap = new Map((venues ?? []).map((v) => [v.id, v.full_name as string | null]));
      const allocMap = new Map((allocs ?? []).map((a) => [a.shift_id, a.status as string]));
      setRows(list.map((p) => {
        const s = shiftMap.get(p.shift_id as string);
        return {
          payout_id: p.id as string,
          shift_id: p.shift_id as string,
          amount_pence: p.amount_pence as number,
          payout_status: p.status as string,
          failure_reason: p.failure_reason as string | null,
          paid_out_at: p.paid_out_at as string | null,
          created_at: p.created_at as string,
          shift_start: s?.start_time ?? null,
          shift_end: s?.end_time ?? null,
          role_type: s?.role_type ?? null,
          venue_name: s?.venue_id ? venueMap.get(s.venue_id) ?? null : null,
          tip_allocation_status: allocMap.get(p.shift_id as string) ?? null,
        };
      }));
      setLoading(false);
    })();
  }, [navigate]);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-5xl px-6 py-12 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Gratuity Register</p>
            <h1 className="mt-3 font-display text-3xl lg:text-4xl">My Tips</h1>
            <span className="gold-divider mt-4" />
            <p className="mt-4 text-sm text-muted-foreground max-w-xl">
              Every tip allocated to you under the UK Tipping Act 2023 — 100% pass-through,
              no platform fee. Status updates automatically as Stripe processes the transfer.
            </p>
          </div>
          <Link to="/bartender" className="border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
            ← Engagements
          </Link>
        </div>

        <div className="mt-10 space-y-3">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && rows.length === 0 && (
            <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No tips have been allocated to you yet.
            </div>
          )}
          {rows.map((r) => {
            const start = r.shift_start ? new Date(r.shift_start) : null;
            const info = statusLabel(r.payout_status, r.tip_allocation_status);
            return (
              <article key={r.payout_id} className="border border-border bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className={`text-xs tracking-wide-luxury ${info.tone}`}>{info.label}</p>
                    <h2 className="mt-1 font-display text-lg capitalize">
                      {r.role_type ? r.role_type.replace(/_/g, " ") : "Engagement"}
                      {start ? ` · ${start.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {r.venue_name ? `${r.venue_name} · ` : ""}Allocated {new Date(r.created_at).toLocaleDateString("en-GB")}
                      {r.paid_out_at ? ` · Paid out ${new Date(r.paid_out_at).toLocaleDateString("en-GB")}` : ""}
                    </p>
                    {r.payout_status === "failed" && r.failure_reason && (
                      <p className="mt-2 text-xs text-destructive">Reason: {r.failure_reason}</p>
                    )}
                  </div>
                  <p className="font-display text-xl">£{(r.amount_pence / 100).toFixed(2)}</p>
                </div>
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
